import React, { useState } from 'react';
import { Users, X, Send, CheckCircle, Phone, Mail, MapPin } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/useAuthStore';

interface BrokerConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyAddress?: string;
  valuationId?: string;
  stationObjectId?: string;
}

export function BrokerConnectionModal({
  isOpen,
  onClose,
  propertyAddress = '',
  valuationId = '',
  stationObjectId = '',
}: BrokerConnectionModalProps) {
  const { user } = useAuthStore();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setName('');
      setPhone('');
      setEmail(user?.email || '');
      setMessage('');
      setIsSubmitted(false);
      setError(null);
    }, 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Pack broker details into the description field since the feedback table
      // doesn't have a metadata column
      const brokerDetails = [
        `[BROKER INQUIRY]`,
        `Name: ${name}`,
        `Phone: ${phone}`,
        `Email: ${email}`,
        `Property: ${propertyAddress}`,
        stationObjectId ? `Station ID: ${stationObjectId}` : '',
        valuationId ? `Valuation ID: ${valuationId}` : '',
        message.trim() ? `\nMessage: ${message.trim()}` : '',
      ].filter(Boolean).join('\n');

      const { error: insertError } = await supabase
        .from('feedback')
        .insert({
          type: 'other',
          description: brokerDetails,
          url: window.location.href,
          user_agent: navigator.userAgent,
          screen_size: `${window.innerWidth}x${window.innerHeight}`,
          user_id: user?.id || null,
          user_email: email || user?.email || null,
          created_at: new Date().toISOString(),
          status: 'new',
        });

      if (insertError) throw insertError;

      setIsSubmitted(true);

      // Auto-close after 4 seconds
      setTimeout(() => {
        handleClose();
      }, 4000);
    } catch (err: any) {
      console.error('Error submitting broker inquiry:', err);
      setError('Failed to submit your request. Please try again or email us directly at contact@thefuelcentral.com.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-blue-100 p-2 rounded-full">
              {isSubmitted ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <Users className="w-5 h-5 text-blue-600" />
              )}
            </div>
            <h2 className="text-xl font-semibold">
              {isSubmitted ? 'Request Submitted' : 'Connect with a Local Broker'}
            </h2>
          </div>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isSubmitted ? (
            <div className="text-center py-8">
              <div className="mx-auto mb-6 bg-green-100 p-4 rounded-full inline-flex justify-center items-center">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">We've Received Your Request!</h3>
              <p className="text-gray-600 mb-6">
                A local broker specialist will review your inquiry and reach out within 2 business days.
                Thank you for using Fuel Central.
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <p className="text-gray-600 text-sm mb-4">
                Fill out the form below and a qualified gas station broker in your area will be in touch.
              </p>

              {propertyAddress && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-blue-600 font-medium">Property</p>
                    <p className="text-sm text-blue-800">{propertyAddress}</p>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4 text-sm text-red-700">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="broker-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="broker-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="John Smith"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="broker-phone" className="block text-sm font-medium text-gray-700 mb-1">
                      <Phone className="w-3.5 h-3.5 inline mr-1" />
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="broker-phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label htmlFor="broker-email" className="block text-sm font-medium text-gray-700 mb-1">
                      <Mail className="w-3.5 h-3.5 inline mr-1" />
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="broker-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="broker-message" className="block text-sm font-medium text-gray-700 mb-1">
                    Message (optional)
                  </label>
                  <textarea
                    id="broker-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="I'm interested in buying/selling this property..."
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Submit Request
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
